import type { ComputedRef, InjectionKey, Ref } from "vue";
import type { DataItem, TabBoxConfig, ToggleButtonConfig, UIColor } from "..";

export const RadioButtonModelInjectionKey = Symbol(
  "radioButtonModelInjectionKey"
) as InjectionKey<Ref<string | number | undefined>>;

export const ToggleButtonConfigInjectionKey = Symbol(
  "toggleButtonConfigInjectionKey"
) as InjectionKey<ComputedRef<ToggleButtonConfig>>;

export const TabBoxConfigInjectionKey = Symbol(
  "tabBoxConfigInjectionKey"
) as InjectionKey<ComputedRef<TabBoxConfig>>;

export const CompositionBackgroundCardInjectionKey = Symbol(
  "compositionBackgroundCardInjectionKey"
) as InjectionKey<Ref<boolean>>;

export const SimpleTableContextInjectionKey = Symbol(
  "simpleTableContextInjectionKey"
) as InjectionKey<{
  containsRenderAsCard: Ref<boolean>;
  containsRenderAsCardExtension: Ref<boolean>;
}>;

export const isDataItem = (item: any): item is DataItem<string> =>
  !!item && typeof item === "object" && "id" in item;

export const isEqualItem = <T, K>(a: T, b: K) => {
  return isDataItem(a) && !isDataItem(b)
    ? b === a.id
    : isDataItem(b) && !isDataItem(a)
    ? b.id === a
    : isDataItem(b) && isDataItem(a)
    ? b.id === a.id
    : (b as any) === (a as any);
};

/**
 * Explicitly mapped CSS variable names to Tailwind classes for each prefix.
 */
export const bgCssVarToTailwindClass: Record<string, string> = {
  "--color-white": "bg-white",
  "--color-primary": "bg-primary",
  "--color-accent-100": "bg-accent-100",
  "--color-accent-200": "bg-accent-200",
  "--color-accent-300": "bg-accent-300",
  "--color-accent-400": "bg-accent-400",
  "--color-accent-500": "bg-accent-500",
  "--color-accent-600": "bg-accent-600",
  "--color-accent-700": "bg-accent-700",
  "--color-accent-800": "bg-accent-800",
  "--color-accent-900": "bg-accent-900",
  "--color-accent-950": "bg-accent-950",
  "--color-cyan": "bg-cyan",
  "--color-magenta": "bg-magenta",
  "--color-yellow": "bg-yellow",
  "--color-menu-background": "bg-menu-background",
  "--color-fancy": "bg-fancy",
  "--color-yellow-100": "bg-yellow-100",
  "--color-yellow-200": "bg-yellow-200",
  "--color-yellow-300": "bg-yellow-300",
  "--color-red-100": "bg-red-100",
  "--color-red-200": "bg-red-200",
  "--color-red-300": "bg-red-300",
  "--color-blue-100": "bg-blue-100",
  "--color-blue-200": "bg-blue-200",
  "--color-blue-300": "bg-blue-300",
  "--color-green-100": "bg-green-100",
  "--color-green-200": "bg-green-200",
  "--color-green-300": "bg-green-300",
};

export const textCssVarToTailwindClass: Record<string, string> = {
  "--color-white": "text-white",
  "--color-primary": "text-primary",
  "--color-accent-100": "text-accent-100",
  "--color-accent-200": "text-accent-200",
  "--color-accent-300": "text-accent-300",
  "--color-accent-400": "text-accent-400",
  "--color-accent-500": "text-accent-500",
  "--color-accent-600": "text-accent-600",
  "--color-accent-700": "text-accent-700",
  "--color-accent-800": "text-accent-800",
  "--color-accent-900": "text-accent-900",
  "--color-accent-950": "text-accent-950",
  "--color-cyan": "text-cyan",
  "--color-magenta": "text-magenta",
  "--color-yellow": "text-yellow",
  "--color-menu-background": "text-menu-background",
  "--color-fancy": "text-fancy",
  "--color-yellow-100": "text-yellow-100",
  "--color-yellow-200": "text-yellow-200",
  "--color-yellow-300": "text-yellow-300",
  "--color-red-100": "text-red-100",
  "--color-red-200": "text-red-200",
  "--color-red-300": "text-red-300",
  "--color-blue-100": "text-blue-100",
  "--color-blue-200": "text-blue-200",
  "--color-blue-300": "text-blue-300",
  "--color-green-100": "text-green-100",
  "--color-green-200": "text-green-200",
  "--color-green-300": "text-green-300",
};

export const borderCssVarToTailwindClass: Record<string, string> = {
  "--color-white": "border-white",
  "--color-primary": "border-primary",
  "--color-accent-100": "border-accent-100",
  "--color-accent-200": "border-accent-200",
  "--color-accent-300": "border-accent-300",
  "--color-accent-400": "border-accent-400",
  "--color-accent-500": "border-accent-500",
  "--color-accent-600": "border-accent-600",
  "--color-accent-700": "border-accent-700",
  "--color-accent-800": "border-accent-800",
  "--color-accent-900": "border-accent-900",
  "--color-accent-950": "border-accent-950",
  "--color-cyan": "border-cyan",
  "--color-magenta": "border-magenta",
  "--color-yellow": "border-yellow",
  "--color-menu-background": "border-menu-background",
  "--color-fancy": "border-fancy",
  "--color-yellow-100": "border-yellow-100",
  "--color-yellow-200": "border-yellow-200",
  "--color-yellow-300": "border-yellow-300",
  "--color-red-100": "border-red-100",
  "--color-red-200": "border-red-200",
  "--color-red-300": "border-red-300",
  "--color-blue-100": "border-blue-100",
  "--color-blue-200": "border-blue-200",
  "--color-blue-300": "border-blue-300",
  "--color-green-100": "border-green-100",
  "--color-green-200": "border-green-200",
  "--color-green-300": "border-green-300",
};

/**
 * Returns the closest Tailwind class for a given CSS variable name.
 * @param cssVar CSS variable name (e.g. '--color-blue-500')
 * @param prefix Optional Tailwind prefix (e.g. 'bg', 'border', 'text')
 */
export function getTailwindClassForCssVar(
  cssVar: UIColor,
  prefix: "bg" | "border" | "text" = "text"
): string | undefined {
  if (prefix === "bg") {
    return bgCssVarToTailwindClass[cssVar];
  }
  if (prefix === "border") {
    return borderCssVarToTailwindClass[cssVar];
  }
  // default to text
  return textCssVarToTailwindClass[cssVar];
}
